import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, Search, Server, Zap, Gamepad2, Globe,
  HardDrive, Headphones, Users, TrendingUp, Clock,
} from 'lucide-react';
import { ReviewForestWidget } from '@/components/ReviewForestWidget';

const PRODUCT_ROWS = [
  { icon: Gamepad2,  name: 'Game Server',   sub: 'CS2, Minecraft, ARK & 100+ games',     price: 'from €1.00', href: '/gameservers', color: 'text-green-400' },
  { icon: Server,    name: 'KVM VPS',        sub: 'AMD Ryzen, NVMe, full root access',     price: 'from €2.35', href: '/vps',         color: 'text-primary'   },
  { icon: HardDrive, name: 'Root Server',    sub: 'Dedicated hardware, no resource sharing', price: 'from €9.99', href: '/rootserver',  color: 'text-blue-400'  },
  { icon: Globe,     name: 'Web Hosting',    sub: 'SSD/NVMe, PHP 8, MySQL',               price: 'from €2.99', href: '/webhosting',  color: 'text-purple-400' },
  { icon: Headphones,'name': 'TeamSpeak 3', sub: '10–512 slots, instant online',          price: 'from €1.90', href: '/teamspeak',   color: 'text-yellow-400' },
];

const STATS = [
  { icon: Clock,      value: 'Since 2013', label: 'In business',       color: 'text-primary'    },
  { icon: TrendingUp, value: '99.2%',      label: 'Uptime SLA',        color: 'text-blue-400'   },
  { icon: Zap,        value: 'up to 10 Gbit/s',  label: 'Network bandwidth', color: 'text-yellow-400' },
];

export const Hero = () => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-[90vh] flex flex-col justify-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 hero-glow" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(255,107,0,0.08),transparent_55%)]" />
      <div
        className="absolute inset-0 opacity-[0.025]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.15) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.15) 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}
      />

      {/* Main content */}
      <div className="container-main relative z-10 py-16">
        <div className="grid lg:grid-cols-[1fr_420px] xl:grid-cols-[1fr_460px] gap-10 xl:gap-16 items-center">

          {/* ── Left ── */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8 border border-primary/20"
            >
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{t('hero.tagline')}</span>
            </motion.div>

            {/* Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-none mb-6">
              {t('hero.title')}
              <br />
              <span className="text-gradient">{t('hero.titleHighlight')}</span>
            </h1>

            {/* Description */}
            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mb-10 leading-relaxed">
              {t('hero.description')}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/gameservers">
                <Button
                  size="lg"
                  className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white font-bold uppercase tracking-wider h-14 px-8 glow-hover"
                  data-testid="hero-cta-primary"
                >
                  {t('hero.cta')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/product-finder">
                <Button
                  variant="outline"
                  size="lg"
                  className="w-full sm:w-auto border-2 border-primary/30 hover:border-primary h-14 px-8"
                  data-testid="hero-cta-secondary"
                >
                  <Search className="w-5 h-5 mr-2" />
                  {t('hero.ctaSecondary')}
                </Button>
              </Link>
            </div>

          </motion.div>

          {/* ── Right: Product Dashboard ── */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="hidden lg:block"
          >
            <div
              className="rounded-2xl overflow-hidden border border-white/10"
              style={{ background: 'rgba(10,10,10,0.85)', backdropFilter: 'blur(24px)' }}
            >
              {/* Terminal top bar */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/8 bg-white/3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-500/60" />
                    <span className="w-3 h-3 rounded-full bg-yellow-500/60" />
                    <span className="w-3 h-3 rounded-full bg-green-500/60" />
                  </div>
                  <span className="text-xs font-mono text-white/30 ml-1.5">dsh — products</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="text-xs font-mono text-green-400/70">online</span>
                </div>
              </div>

              {/* Product rows */}
              <div>
                {PRODUCT_ROWS.map((row, i) => (
                  <motion.div
                    key={row.name}
                    initial={{ opacity: 0, x: 16 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.35 + i * 0.07 }}
                  >
                    <Link
                      to={row.href}
                      data-testid={`hero-product-row-${i}`}
                      className="flex items-center gap-4 px-5 py-4 border-b border-white/6 hover:bg-white/4 transition-colors duration-200 group"
                    >
                      {/* Icon */}
                      <div className={`w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0 border border-white/8 group-hover:border-white/15 transition-colors`}>
                        <row.icon className={`w-4 h-4 ${row.color}`} />
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white/90 truncate">{row.name}</div>
                        <div className="text-xs text-white/35 truncate mt-0.5">{row.sub}</div>
                      </div>

                      {/* Price + arrow */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`text-sm font-black ${row.color}`}>{row.price}</span>
                        <ArrowRight className="w-3.5 h-3.5 text-white/20 group-hover:text-white/60 transition-colors -translate-x-1 group-hover:translate-x-0 duration-200" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              {/* Stats footer */}
              <div className="grid grid-cols-3 border-t border-white/8">
                {[
                  { val: '99.9%', label: 'Uptime',   color: 'text-green-400' },
                  { val: '10G',   label: 'Network',  color: 'text-primary'   },
                  { val: 'Mo–Fr', label: '8–12 & 14–18', color: 'text-blue-400' },
                ].map((s, i) => (
                  <div
                    key={s.label}
                    className={`text-center py-4 ${i < 2 ? 'border-r border-white/8' : ''}`}
                  >
                    <div className={`text-lg font-black ${s.color}`}>{s.val}</div>
                    <div className="text-xs text-white/30 mt-0.5">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>

        {/* ── Stats Strip ── */}
          <motion.div
              initial={{opacity: 0, y: 16}}
              animate={{opacity: 1, y: 0}}
              transition={{delay: 0.6}}
              className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4"
              data-testid="hero-stats-strip"
          >
              {STATS.map((stat, i) => (
                  <div
                      key={stat.label}
                      className="flex items-center gap-4 p-5 rounded-2xl border border-border bg-card/60 backdrop-blur-sm"
                  >
                      <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center shrink-0">
                          <stat.icon className={`w-5 h-5 ${stat.color}`}/>
                      </div>
                      <div>
                          <div className={`text-xl font-black leading-none ${stat.color}`}>{stat.value}</div>
                          <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
                      </div>
                  </div>
              ))}

              {/* ReviewForest reviews tile */}
              <div
                  className="flex items-center justify-center p-5 rounded-2xl border border-border bg-card/60 backdrop-blur-sm"
                  data-testid="hero-reviews-tile"
              >
                  <ReviewForestWidget stacked />
              </div>
          </motion.div>

      </div>
    </section>
  );
};

export default Hero;
